/**
 * Vector tiles of the 1/2000 zoning plans (QH phân khu, layer 654) and of the detailed plans
 * (QH chi tiết, layer 673) in public/data/qhv, written by scripts/build-vector.mjs:
 *   <z>/<x>/<y>.pbf  Mapbox vector tile, source layers "qhpk" / "qhct". z9–13 carry { t } only;
 *                    z14 (extent 8192, overzoomed above) adds feature ids and the hover-card fields
 *                    k ký hiệu, a m², h tầng cao, m mật độ XD (%), s hệ số SDĐ, q hồ sơ QH,
 *                    kh khu QH, st trạng thái, ds dân số.
 *   manifest.json    { v, generated, source, minzoom, maxzoom, bounds, layers, types, grids }
 *                    types[t] = { l: layerNo, n: category (phanLoai), c: "rgba()", raw, g: loaiDat,
 *                    cnt, m2 }; grids use the label-tile bitsets (core/labeltile.js).
 * Pure helpers (no DOM, no MapLibre) shared by map.js, layers.js and hovercard.js; selftest covers them.
 */
import { fold } from './text.js';
import { parseLabelManifest, labelTileState } from './labeltile.js';

/** plan (layer number) -> source layer of the vector tiles */
export const VLAYER = Object.freeze({ 654: 'qhpk', 673: 'qhct' });
/** the hover card needs the z14 attributes and a zoom where single cells can be pointed at */
export const CARD_MINZOOM = 15;
const FALLBACK = 'rgba(148,163,184,0.4)';

/** manifest.json -> parsed manifest, null when unusable */
export function parseVectorManifest(j) {
  if (!j || !Array.isArray(j.types) || !j.layers || !j.grids) return null;
  const g = parseLabelManifest(j);
  if (!g) return null;
  return {
    v: String(j.v || ''), generated: j.generated || null, source: j.source || null,
    minzoom: j.minzoom ?? 9, maxzoom: j.maxzoom ?? 14, bounds: j.bounds || null,
    layers: j.layers, types: j.types, grids: g.grids,
  };
}

/** Only tiles flagged in the grid exist; anything else (outside the grid, 'empty') is skipped. */
export const vectorTileHas = (man, z, x, y) => labelTileState(man, z, x, y) === 'have';

/** Source layer serving `plan` from this manifest, or null (the plan stays a raster). */
export function vectorLayerOf(man, plan) {
  const sl = VLAYER[plan];
  return sl && man?.layers?.[sl] ? sl : null;
}

const opaque = (c) => String(c).replace(/^rgba\((\d+),(\d+),(\d+),[\d.]+\)$/, 'rgb($1,$2,$3)');

/**
 * fill-color of a source layer: type index -> official colour, its alpha included (`solid`: alpha
 * dropped, for the legend highlight drawn over the faded layer).
 */
export function fillColorExpr(man, plan, solid = false) {
  const pairs = [];
  (man?.types || []).forEach((t, i) => { if (t.l === plan) pairs.push(i, solid ? opaque(t.c || FALLBACK) : t.c || FALLBACK); });
  const fb = solid ? opaque(FALLBACK) : FALLBACK;
  return pairs.length ? ['match', ['get', 't'], ...pairs, fb] : fb;
}

/**
 * Type indices drawn with a legend item. The 1/2000 legend is keyed by loaiDat (types[].g), the
 * detailed-plan legend by phanLoai (types[].n); the official colour is the fallback key.
 */
export function typesForLegendItem(man, plan, legend, item) {
  if (!man || !item) return [];
  const key = legend?.field === 'loaiDat' ? 'g' : 'n';
  const want = new Set([fold(item.value || ''), fold(item.label || '')].filter(Boolean));
  const out = [];
  man.types.forEach((t, i) => { if (t.l === plan && t[key] && want.has(fold(t[key]))) out.push(i); });
  if (!out.length && item.raw) {
    const raw = String(item.raw).toUpperCase();
    man.types.forEach((t, i) => { if (t.l === plan && t.raw === raw) out.push(i); });
  }
  return out;
}

/** { cells, m2 } of a set of type indices */
export function typeStats(man, ts) {
  let cells = 0, m2 = 0;
  for (const i of ts || []) { const t = man?.types?.[i]; if (t) { cells += t.cnt || 0; m2 += t.m2 || 0; } }
  return { cells, m2 };
}

/**
 * Opacities for the overlay opacity `op`: { base } of the whole layer (faded while a legend item is
 * highlighted) and { pick } of the solid highlight fill of the picked types.
 */
export function vectorOpacity(op, ts, dim = 0.3) {
  const r = (v) => Math.round(v * 1000) / 1000;
  return ts?.length ? { base: r(op * dim), pick: r(Math.min(1, 0.25 + 0.45 * op)) } : { base: op, pick: 0 };
}

const kText = (k) => (k == null ? '' : String(k).trim());
const PLAN_WORD = { 654: 'QH phân khu', 673: 'QH chi tiết' };

/**
 * Hover-card content of a cell (properties of a z14 feature):
 *   { title, code, color, facts: [area, tầng cao, mật độ], ctx: [trạng thái, hồ sơ, khu] }
 * `area` formats m² (ui.js fmt.area). Lines without data are dropped.
 */
export function cardModel(props, type, plan, { area = (m2) => `${Math.round(m2)} m²` } = {}) {
  const p = props || {};
  const facts = [];
  const a = +p.a;
  if (Number.isFinite(a) && a > 0) facts.push(area(a));
  if (kText(p.h)) facts.push(`Tầng cao ${kText(p.h)}`);
  if (kText(p.m)) facts.push(`Mật độ XD ${kText(p.m)}%`);
  if (Number.isFinite(+p.s) && +p.s > 0 && facts.length < 3) facts.push(`HSSDĐ ${String(+p.s).replace('.', ',')}`);
  const ctx = [];
  if (kText(p.st)) ctx.push(kText(p.st));
  if (kText(p.q)) ctx.push(`${PLAN_WORD[plan] || 'QH'} ${kText(p.q)}`);
  const kh = kText(p.kh);
  if (kh) ctx.push(/^(khu|ô)\b/i.test(kh) ? kh : `Khu ${kh}`);
  return {
    title: type?.n || 'Ô quy hoạch',
    code: kText(p.k) || null,
    color: /^#[0-9A-F]{6}/i.test(type?.raw || '') ? type.raw.slice(0, 7) : '#94a3b8', // solid swatch
    facts,
    ctx,
  };
}

/**
 * Where the card goes: right of / below the pointer, flipped near the edges of the map box
 * (w × h), never outside it. Returns the top-left corner.
 */
export function cardPosition(px, py, cw, ch, w, h, gap = 16) {
  let x = px + gap, y = py + gap;
  if (x + cw > w - 6) x = px - gap - cw;
  if (y + ch > h - 6) y = py - gap - ch;
  return { x: Math.max(6, Math.min(x, w - cw - 6)), y: Math.max(6, Math.min(y, h - ch - 6)) };
}
